import { doc, updateDoc, arrayUnion, collection, addDoc, getDocs, getDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

// Save loan — deduplicated by reference so it never saves twice even if called twice
export async function saveLoanApplication({ uid, userName, userEmail, userPhone, loanAmount, loanFee, reference, loanType, idNumber, dueDate }) {
  try {
    // First check if this reference already exists (prevent duplicates from StrictMode double-invoke)
    const userRef  = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const existing = (userSnap.data().loans || []).find(l => l.reference === reference);
      if (existing) {
        console.info('[Loan] Already saved (duplicate prevented):', reference);
        return { success: true, duplicate: true };
      }
    }

    const loanEntry = {
      id:         reference,
      reference,
      loanAmount: Number(loanAmount),
      loanFee:    Number(loanFee),
      loanType:   loanType || 'Personal Loan',
      idNumber:   idNumber || '—',
      dueDate,
      status:     'pending',
      feePaid:    true,          // fee was paid via Payhero STK push
      createdAt:  new Date().toISOString(),
    };

    // PRIMARY: write to user's own document (always permitted)
    await updateDoc(userRef, { loans: arrayUnion(loanEntry) });
    console.info('[Loan] Saved to user doc:', reference);

    // SECONDARY: global loans collection for admin view (best effort)
    try {
      await addDoc(collection(db, 'loans'), { ...loanEntry, uid, userName, userEmail, userPhone });
    } catch (_) {}

    return { success: true };
  } catch (error) {
    console.error('[Loan] Failed to save loan:', error);
    return { success: false, error };
  }
}

// Fetch user loans — from user doc, deduplicated by reference
export async function fetchUserLoans(uid) {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return [];
    const loans = snap.data().loans || [];
    // Deduplicate by reference (safety net)
    const seen = new Set();
    const unique = loans.filter(l => {
      const key = l.reference || l.id || JSON.stringify(l);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return unique.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (error) {
    console.error('[Loan] fetchUserLoans failed:', error);
    return [];
  }
}

export async function fetchAllLoans() {
  try {
    const q = query(collection(db, 'loans'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('[Loan] fetchAllLoans failed:', error);
    return [];
  }
}

// Support saved under user's own subcollection — always permitted
export async function saveSupportMessage({ uid, userName, userEmail, subject, message, category }) {
  try {
    await addDoc(collection(db, 'users', uid, 'support'), {
      uid, userName, userEmail, subject, message, category,
      status: 'open',
      createdAt: new Date().toISOString(),
    });
    return { success: true };
  } catch (error) {
    console.error('[Support] Failed:', error);
    return { success: false, error };
  }
}
